/**
 * PublishingApprovals — the editorial gate. Approve for transmission or send
 * an episode back to editing, with the outstanding checks spelled out.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, Undo2 } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

export const PublishingApprovals = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['fwtv-approvals'],
    queryFn: () => api.get('/fwtv/studio/approvals').then((r) => r.data),
  });

  const move = useMutation({
    mutationFn: ({ id, stage }) => api.post(`/fwtv/studio/episodes/${id}/stage`, { stage }),
    onSuccess: (_r, v) => {
      toast.success(v.stage === 'approved' ? 'Approved for transmission' : 'Returned to editing');
      ['fwtv-approvals', 'fwtv-episodes', 'fwtv-studio-overview'].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not update that episode')),
  });

  const awaiting = data?.awaiting || [];
  const approved = data?.approved || [];

  const Row = ({ ep, isApproved }) => {
    const missing = Object.entries(ep.readiness?.checks || {})
      .filter(([, ok]) => !ok).map(([k]) => k);
    return (
      <Card data-testid={`approval-${ep.id}`}>
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm">{ep.title}</p>
            <p className="text-xs text-slate-500">
              {ep.programme_title}
              {missing.length ? ` · outstanding: ${missing.join(', ')}` : ' · all checks complete'}
            </p>
          </div>
          {isApproved ? (
            <>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Approved</Badge>
              <Button variant="outline" size="sm" data-testid={`approval-return-${ep.id}`}
                      onClick={() => move.mutate({ id: ep.id, stage: 'editing' })}>
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Return to editing
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" data-testid={`approval-back-${ep.id}`}
                      onClick={() => move.mutate({ id: ep.id, stage: 'editing' })}>
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Return to editing
              </Button>
              <Button size="sm" disabled={!data?.can_approve} data-testid={`approval-approve-${ep.id}`}
                      onClick={() => move.mutate({ id: ep.id, stage: 'approved' })}>
                <Check className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="fwtv-approvals">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
          Awaiting approval
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Complete media checks and valid rights before approval.
        </p>
        <div className="mt-4 space-y-3">
          {awaiting.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="approvals-empty">
              Nothing is waiting on the editorial gate.
            </p>
          ) : awaiting.map((ep) => <Row key={ep.id} ep={ep} isApproved={false} />)}
        </div>
      </div>

      {approved.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
            Approved for transmission
          </h3>
          <div className="mt-4 space-y-3">
            {approved.map((ep) => <Row key={ep.id} ep={ep} isApproved />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublishingApprovals;
